import { loadPixelBase } from './metaPixelLoader';
import { hasMarketingConsent } from "@/utils/consent";

declare global {
  interface Window {
    fbq: any;
    _fbInitialized?: boolean;
  }
}

const PIXEL_ID = '449312234890267';

const isPixelAvailable = () => {
  return typeof window !== 'undefined' && window.fbq;
};

export const initializeMetaPixel = () => {
  try {
    if (!hasMarketingConsent()) return;
    loadPixelBase();          // ensure stub + remote script are injected

    if (!isPixelAvailable()) {
      console.warn('Meta Pixel not available');
      return;
    }

    if (window._fbInitialized) {
      return;
    }

    window.fbq('init', PIXEL_ID);
    window._fbInitialized = true;
  } catch (error) {
    console.error('Error initializing Meta Pixel:', error);
  }
};

export const trackPageView = () => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'PageView');
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track when users view specific service pages
export const trackServiceView = (serviceName: string) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'ViewContent', {
      content_type: 'dental_service',
      content_name: serviceName
    });
  } catch (error) {
    console.error('Error tracking service view:', error);
  }
};

// Track contact form submissions
export const trackContactFormSubmission = (formType: string) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'Lead', {
      content_category: 'contact',
      content_name: formType
    });
  } catch (error) {
    console.error('Error tracking contact form submission:', error);
  }
};

// Track appointment requests
export const trackAppointmentRequest = (serviceType?: string) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'Schedule', {
      content_category: 'appointment',
      content_name: serviceType || 'general'
    });
  } catch (error) {
    console.error('Error tracking appointment request:', error);
  }
};

// Track when users engage with pricing information
export const trackPricingView = (serviceType: string) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'ViewContent', {
      content_type: 'pricing',
      content_name: serviceType
    });
  } catch (error) {
    console.error('Error tracking pricing view:', error);
  }
};

// Track when users start filling out forms
export const trackFormStart = (formType: string) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'InitiateCheckout', {
      content_category: 'form_start',
      content_name: formType
    });
  } catch (error) {
    console.error('Error tracking form start:', error);
  }
};

// Track successful form completions
export const trackFormComplete = (formType: string) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', 'CompleteRegistration', {
      content_category: 'form_complete',
      content_name: formType
    });
  } catch (error) {
    console.error('Error tracking form completion:', error);
  }
};

export const trackEvent = (eventName: string, params?: object) => {
  try {
    if (!hasMarketingConsent()) return;
    if (!isPixelAvailable()) return;
    window.fbq('track', eventName, params);
  } catch (error) {
    console.error('Error tracking custom event:', error);
  }
}; 